// gioco/spettatori.ts — torneo: il video della finale a chi guarda (i due eliminati).
//
// Ogni finalista manda il suo video (con il filtro) direttamente a ciascuno spettatore, come nella videochiamata:
// niente passa dal server di gioco, che inoltra solo i messaggi per mettersi d'accordo. Lo spettatore propone
// (offerta "solo ricevere") a tutti e due i finalisti; il finalista risponde "solo inviare".
import type { DatiSegnale, Spettatore } from "../../../condiviso/protocollo.ts";
import type { ServerIce } from "../servizi/sfide.ts";

type Ice = () => Promise<ServerIce[]>;

const candidato = (c: RTCIceCandidate | null): DatiSegnale => ({
  candidato: c ? { candidate: c.candidate, sdpMid: c.sdpMid, sdpMLineIndex: c.sdpMLineIndex, usernameFragment: c.usernameFragment } : null,
});

/** Lato finalista: una connessione "solo inviare" per ogni spettatore che chiede il video. */
export class Trasmissioni {
  private pc = new Map<Spettatore, RTCPeerConnection>();
  /** I messaggi di ogni spettatore uno dopo l'altro (i candidati non devono superare l'offerta). */
  private code = new Map<Spettatore, Promise<void>>();
  /** Candidati arrivati prima che la connessione abbia la descrizione dello spettatore. */
  private attesa = new Map<Spettatore, DatiSegnale[]>();
  private flusso: MediaStream;
  private readonly ice: Ice;
  private readonly invia: (con: Spettatore, d: DatiSegnale) => void;
  private chiuse = false;

  constructor(flusso: MediaStream, ice: Ice, invia: (con: Spettatore, d: DatiSegnale) => void) {
    this.flusso = flusso;
    this.ice = ice;
    this.invia = invia;
  }

  ricevi(da: Spettatore, d: DatiSegnale): Promise<void> {
    const prima = this.code.get(da) ?? Promise.resolve();
    const questa = prima.then(() => this.gestisci(da, d));
    this.code.set(da, questa);
    return questa;
  }

  private async gestisci(da: Spettatore, d: DatiSegnale): Promise<void> {
    if (this.chiuse) return;
    try {
      if ("descrizione" in d) {
        if (d.descrizione.type !== "offer") return;
        // Ogni offerta è una connessione nuova (lo spettatore la rifà se si perde).
        this.pc.get(da)?.close();
        this.pc.delete(da);
        this.attesa.set(da, []);
        const server = await this.ice();
        if (this.chiuse) return;
        const pc = new RTCPeerConnection({ iceServers: server.map((s) => ({ ...s })) });
        this.pc.set(da, pc);
        pc.onicecandidate = (ev) => this.invia(da, candidato(ev.candidate));
        await pc.setRemoteDescription(d.descrizione);
        for (const c of this.attesa.get(da)?.splice(0) ?? []) if ("candidato" in c) await pc.addIceCandidate(c.candidato ?? undefined).catch(() => undefined);
        for (const t of this.flusso.getTracks()) pc.addTrack(t, this.flusso);
        for (const tr of pc.getTransceivers()) if (tr.direction === "sendrecv") tr.direction = "sendonly";
        await pc.setLocalDescription();
        const loc = pc.localDescription;
        if (loc && loc.type === "answer") this.invia(da, { descrizione: { type: "answer", sdp: loc.sdp } });
      } else {
        const pc = this.pc.get(da);
        if (pc?.remoteDescription) await pc.addIceCandidate(d.candidato ?? undefined);
        else this.attesa.get(da)?.push(d);
      }
    } catch {
      // offerta non valida o fuori ordine: lo spettatore riproverà
    }
  }

  /** Il video inviato cambia (filtro acceso o spento): si sostituisce la traccia, senza rinegoziare. */
  cambiaFlusso(flusso: MediaStream): void {
    this.flusso = flusso;
    const video = flusso.getVideoTracks()[0];
    if (!video) return;
    for (const pc of this.pc.values()) {
      const s = pc.getSenders().find((x) => x.track?.kind === "video");
      if (s && s.track !== video) void s.replaceTrack(video).catch(() => undefined);
    }
  }

  /** Chi guarda adesso: le connessioni di chi se n'è andato si chiudono. */
  presenti(posti: readonly Spettatore[]): void {
    for (const [p, pc] of this.pc) {
      if (!posti.includes(p)) {
        pc.close();
        this.pc.delete(p);
        this.attesa.delete(p);
      }
    }
  }

  chiudi(): void {
    this.chiuse = true;
    for (const pc of this.pc.values()) pc.close();
    this.pc.clear();
  }
}

/** Lato spettatore: riceve il video di un finalista ("a" o "b"). */
export class Visione {
  private pc: RTCPeerConnection | null = null;
  private readonly flusso = new MediaStream();
  private readonly invia: (d: DatiSegnale) => void;
  private readonly suFlusso: (s: MediaStream | null) => void;
  private readonly suStato: (collegato: boolean) => void;
  private chiusa = false;
  private riprova: ReturnType<typeof setTimeout> | null = null;
  private iceServer: ServerIce[] = [];

  constructor(o: { invia: (d: DatiSegnale) => void; suFlusso: (s: MediaStream | null) => void; suStato: (collegato: boolean) => void }) {
    this.invia = o.invia;
    this.suFlusso = o.suFlusso;
    this.suStato = o.suStato;
  }

  /** (Ri)comincia: offerta "solo ricevere" (audio e video). */
  async avvia(iceServer: ServerIce[]): Promise<void> {
    if (this.chiusa) return;
    this.iceServer = iceServer;
    if (this.riprova) clearTimeout(this.riprova);
    this.riprova = null;
    this.pc?.close();
    for (const t of this.flusso.getTracks()) this.flusso.removeTrack(t);
    const pc = new RTCPeerConnection({ iceServers: iceServer.map((s) => ({ ...s })) });
    this.pc = pc;
    pc.addTransceiver("video", { direction: "recvonly" });
    pc.addTransceiver("audio", { direction: "recvonly" });
    pc.ontrack = (ev) => {
      if (!this.flusso.getTracks().includes(ev.track)) this.flusso.addTrack(ev.track);
      this.suFlusso(this.flusso);
    };
    pc.onicecandidate = (ev) => this.invia(candidato(ev.candidate));
    pc.onconnectionstatechange = () => {
      if (this.pc !== pc) return;
      const s = pc.connectionState;
      this.suStato(s === "connected");
      // Persa: si rifà l'offerta dopo un attimo (il finalista potrebbe essere in un rientro).
      if ((s === "failed" || s === "disconnected") && !this.riprova) {
        this.riprova = setTimeout(() => {
          this.riprova = null;
          if (this.pc === pc && pc.connectionState !== "connected") void this.avvia(this.iceServer);
        }, 3_000);
      }
    };
    try {
      await pc.setLocalDescription();
      const d = pc.localDescription;
      if (d && d.type === "offer" && this.pc === pc) this.invia({ descrizione: { type: "offer", sdp: d.sdp } });
    } catch {
      // si riproverà al prossimo cambio di sessione del finalista
    }
  }

  async ricevi(d: DatiSegnale): Promise<void> {
    const pc = this.pc;
    if (!pc || this.chiusa) return;
    try {
      if ("descrizione" in d) {
        if (d.descrizione.type === "answer" && pc.signalingState === "have-local-offer") await pc.setRemoteDescription(d.descrizione);
      } else await pc.addIceCandidate(d.candidato ?? undefined);
    } catch {
      // risposta di un'offerta vecchia: si ignora
    }
  }

  chiudi(): void {
    this.chiusa = true;
    if (this.riprova) clearTimeout(this.riprova);
    this.pc?.close();
    this.pc = null;
    this.suFlusso(null);
  }
}
